import type { OpenFinding } from '@/lib/conversationReviews.ts';
import type { ConversationOverview, Message } from '@/types/messages.ts';
import { findQuotedMessage } from '@/components/conversationReview/findQuotedMessage.ts';
import {
  needsAttention,
  type AttentionSeverity,
} from '@/components/conversationReview/needsAttention.ts';
import { isUnplaced } from './isUnplaced.ts';

/** An open finding worth a mark, and the message of the transcript it is about. */
export interface PlacedFinding {
  key: string;
  reviewId: string;
  finding: OpenFinding & { severity: AttentionSeverity };
  /** The message it sits under; null while it cannot be told, or when it never can. */
  messageId: string | null;
  /** Whether every message the reviewer read is on screen, so a missing message stays missing. */
  read: boolean;
}

export interface PlaceFindingsInput {
  conversations: ConversationOverview[];
  /** Every message read so far, the internal ones included. */
  loaded: Message[];
  /** What the transcript draws: the only messages a finding can sit under. */
  shown: Message[];
  /** Whether the transcript has been read back to its very first message. */
  complete: boolean;
}

/**
 * The findings of the conversations being read, each under the message its
 * evidence was quoted from, the way the review page's "Show in conversation"
 * finds it — but only among the messages the reviewer read. Findings that
 * cannot be placed come first, so the bar shows them before anything it jumps to.
 */
export function placeFindings({
  conversations,
  loaded,
  shown,
  complete,
}: PlaceFindingsInput): PlacedFinding[] {
  const placed: (PlacedFinding & { at: number })[] = [];

  for (const conversation of conversations) {
    for (const review of conversation.open_reviews) {
      const inSession = (message: Message) =>
        message.conversation_id === conversation.id &&
        Number(message.id) >= review.first_message_id &&
        Number(message.id) <= review.last_message_id;

      const read =
        complete ||
        loaded.some(
          (message) =>
            message.conversation_id === conversation.id &&
            Number(message.id) <= review.first_message_id,
        );
      const candidates = read ? shown.filter(inSession) : [];

      review.findings.forEach((finding, index) => {
        const { severity } = finding;
        if (!needsAttention(severity)) return;

        const quoted = read
          ? findQuotedMessage(candidates, finding, review.user_timezone)
          : null;
        const anchor = quoted ? anchorOf(candidates, quoted, finding.class) : null;
        placed.push({
          key: `${review.id}:${index}`,
          reviewId: review.id,
          finding: { ...finding, severity },
          messageId: anchor ? String(anchor.id) : null,
          read,
          at: anchor ? new Date(anchor.created_at).getTime() : review.first_message_id,
        });
      });
    }
  }

  return placed
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        a.at - b.at ||
        a.key.localeCompare(b.key, undefined, { numeric: true }),
    )
    .map(({ at: _at, ...finding }) => finding);
}

/*
 * Under no message first; then the ones not read back to yet, which are older
 * than anything on screen; then down the transcript.
 */
function rank(finding: PlacedFinding): number {
  if (isUnplaced(finding)) return 0;
  return finding.read ? 2 : 1;
}

/*
 * Wundo not being able to help is about Wundo's answer: when the reviewer quoted
 * the user's request, the note goes under the reply that let them down.
 */
function anchorOf(candidates: Message[], quoted: Message, findingClass: string): Message {
  if (findingClass !== 'wundo_could_not_help' || !isFromUser(quoted)) return quoted;

  const after = candidates.slice(candidates.indexOf(quoted) + 1);
  return (
    after.find(
      (message) => message.type === 'outgoing_message' && message.body.type !== 'pause',
    ) ?? quoted
  );
}

function isFromUser(message: Message): boolean {
  return message.role === 'user' || message.type === 'incoming_message';
}
